import { createSlice, type PayloadAction } from '@reduxjs/toolkit'
import type { EnhancedLanguageId, EnhancedMarketId, EnhancedViewId } from '#api/enhancements/enhancedTmsApiTypes'
import type { FactoryCodeAttributes, FactoryCodeItemId, ListOfMainHeaders } from '#api/tmsApiService'
import type { SerializedError } from '#features/ErrorHandling/errorHelpers'

type FamilyData = {
  status: 'initial' | 'loading' | 'loaded' | 'failed'
  error?: SerializedError
  productData: ListOfMainHeaders
}

type FactoryCodeAttributeData = {
  status: 'initial' | 'loading' | 'loaded' | 'failed'
  error?: SerializedError
  attributes: FactoryCodeAttributes[]
}

export interface FactoryCodesMarketState {
  familiesByLanguage: Record<EnhancedLanguageId, FamilyData>
  factoryCodeAttributes: FactoryCodeAttributeData
}

export interface FactoryCodesState {
  byMarket: Record<EnhancedMarketId, FactoryCodesMarketState | undefined>
}

const initialState: FactoryCodesState = {
  byMarket: {},
}

export const factoryCodesSlice = createSlice({
  name: 'factoryCodes',
  initialState,
  reducers: {
    _setMarketFamilies: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        languageId: EnhancedLanguageId
        productData: ListOfMainHeaders
      }>,
    ) => {
      const { marketId, languageId, productData } = action.payload
      let entry = state.byMarket[marketId]
      if (!entry) {
        entry = state.byMarket[marketId] = {
          factoryCodeAttributes: { status: 'initial', attributes: [] },
          familiesByLanguage: {},
        }
      }
      entry.familiesByLanguage[languageId] = { status: 'loaded', productData, error: undefined }
    },
    _setMarketFamiliesStatus: (
      state,
      action: PayloadAction<
        {
          marketId: EnhancedMarketId
          languageId: EnhancedLanguageId
        } & Pick<FamilyData, 'status' | 'error'>
      >,
    ) => {
      const { marketId, languageId, status, error } = action.payload
      let entry = state.byMarket[marketId]
      if (!entry) {
        entry = state.byMarket[marketId] = {
          factoryCodeAttributes: { status: 'initial', attributes: [] },
          familiesByLanguage: {},
        }
      }
      entry.familiesByLanguage[languageId] = { status, error, productData: entry.familiesByLanguage[languageId]?.productData ?? [] }
    },
    loadFamiliesForMarketAndLanguage: (
      _state,
      _action: PayloadAction<
        { marketId: EnhancedMarketId; languageId: EnhancedLanguageId; viewId: EnhancedViewId; forceReload?: boolean } | undefined
      >,
    ) => {
      // executed in the middleware
    },
    _setMarketFactoryCodeAttributes: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        attributes: FactoryCodeAttributes[]
      }>,
    ) => {
      const { marketId, attributes } = action.payload
      let entry = state.byMarket[marketId]
      if (!entry) {
        entry = state.byMarket[marketId] = {
          factoryCodeAttributes: { status: 'initial', attributes: [] },
          familiesByLanguage: {},
        }
      }
      entry.factoryCodeAttributes = { status: 'loaded', attributes, error: undefined }
    },
    _pushOrReplaceFactoryCodeAttribute: (
      state,
      action: PayloadAction<{
        marketId: EnhancedMarketId
        code: FactoryCodeItemId
        attribute: FactoryCodeAttributes | null
      }>,
    ) => {
      const { marketId, attribute, code } = action.payload
      let entry = state.byMarket[marketId]
      if (!entry) {
        entry = state.byMarket[marketId] = {
          factoryCodeAttributes: { status: 'loaded', attributes: [] },
          familiesByLanguage: {},
        }
      }

      if (!attribute) {
        entry.factoryCodeAttributes.attributes = entry.factoryCodeAttributes.attributes.filter((attr) => attr.code !== code)
        return
      }

      const index = entry.factoryCodeAttributes.attributes.findIndex((attr) => attr.code === attribute?.code)
      if (index !== -1) {
        entry.factoryCodeAttributes.attributes[index] = attribute
      } else {
        entry.factoryCodeAttributes.attributes.push(attribute)
      }
    },
    _setMarketFactoryCodeAttributesStatus: (
      state,
      action: PayloadAction<
        {
          marketId: EnhancedMarketId
        } & Pick<FamilyData, 'status' | 'error'>
      >,
    ) => {
      const { marketId, status, error } = action.payload
      let entry = state.byMarket[marketId]
      if (!entry) {
        entry = state.byMarket[marketId] = {
          factoryCodeAttributes: { status: 'initial', attributes: [] },
          familiesByLanguage: {},
        }
      }
      entry.factoryCodeAttributes = { ...entry.factoryCodeAttributes, status, error }
    },
    loadFactoryCodeAttributesForMarket: (_state, _action: PayloadAction<{ marketId: EnhancedMarketId; forceReload?: boolean } | undefined>) => {
      // executed in the middleware
    },
  },
})

export const {
  loadFamiliesForMarketAndLanguage,
  _setMarketFamiliesStatus,
  _setMarketFamilies,
  loadFactoryCodeAttributesForMarket,
  _setMarketFactoryCodeAttributesStatus,
  _setMarketFactoryCodeAttributes,
  _pushOrReplaceFactoryCodeAttribute,
} = factoryCodesSlice.actions
export const factoryCodesActions = factoryCodesSlice.actions
